import {
  CardCvcElement,
  CardExpiryElement,
  CardNumberElement
} from "@stripe/react-stripe-js";
import { StripeElements } from "@stripe/stripe-js";

import { Dispatch, GetState } from "common/types/redux";

import {
  subscriptionPaymentFailed,
  subscriptionPaymentRequiresAuthentication,
  subscriptionThatRequiresAction
} from "seneca-common/features/subscriptions/state";
import {
  createSubscription,
  retrySubscriptionPayment
} from "seneca-common/features/subscriptions/state/service";
import { ReceivedSubscriptionType } from "seneca-common/features/subscriptions/state/types";

import { PAYMENT_FAILED_ERROR_MESSAGE } from "features/subscriptions/consts";

import { Options } from "../types/HandleSourceCreationOptions";
import { handleAuthentication } from "./handleAuthentication";

export function handlePaymentMethodCreation({
  subscriptionProductId,
  addonProductIds,
  stripePaymentMethodId,
  cardHolderEmail,
  subscriptionId: id,
  options
}: {
  subscriptionProductId: string;
  stripePaymentMethodId: string;
  cardHolderEmail?: string;
  subscriptionId?: string;
  addonProductIds?: string[];
  options: Omit<Options, "stripeElements"> & {
    stripeElements?: StripeElements;
  };
}) {
  const { onSuccess, onError, onRetryPaymentRequired, stripeElements } =
    options;
  return async function (dispatch: Dispatch, getState: GetState) {
    const isRetry = id && subscriptionThatRequiresAction(getState(), id);

    const action = isRetry
      ? retrySubscriptionPayment(id, stripePaymentMethodId, cardHolderEmail)
      : createSubscription({
          stripePaymentMethodId,
          subscriptionProductId,
          cardHolderEmail,
          addonProductIds
        });
    const response = await dispatch(action);

    if (response instanceof Error) {
      clearStripeElements(stripeElements);
      onError && onError(response.message);
      return;
    }

    const state = getState();

    const createdSubscription = response as ReceivedSubscriptionType;
    const { subscriptionId } = createdSubscription;
    if (subscriptionPaymentFailed(state, subscriptionId)) {
      onRetryPaymentRequired(PAYMENT_FAILED_ERROR_MESSAGE);
    } else if (
      subscriptionPaymentRequiresAuthentication(state, subscriptionId)
    ) {
      await dispatch(handleAuthentication({ ...options, subscriptionId }));
    } else {
      onSuccess &&
        onSuccess({
          oldSubscription: createdSubscription
        });
    }
  };
}

function clearStripeElements(stripeElements?: StripeElements) {
  const cardNumberElement = stripeElements?.getElement(CardNumberElement);
  const cardExpiryElement = stripeElements?.getElement(CardExpiryElement);
  const cardCvcElement = stripeElements?.getElement(CardCvcElement);

  return [cardNumberElement, cardExpiryElement, cardCvcElement].map(element =>
    element?.clear()
  );
}
